import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, fmtPrice } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Video, Layers, ArrowRight, Calendar } from 'lucide-react';

const ICONS = { express_chat: Sparkles, express_video: Video };
const CALENDLY_URL = 'https://calendly.com/studiostilo-meeting/sesion-valoracion';

export default function Consultations() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/packages').then(r => setPkgs(r.data));
  }, []);

  const book = async (pkg) => {
    if (!user) { toast.message('Inicia sesión para reservar'); nav('/login?next=/asesorias'); return; }
    setLoading(pkg.id);
    try {
      const r = await api.post('/checkout/session', { package_id: pkg.id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error('No se pudo iniciar el pago');
      setLoading(false);
    }
  };

  return (
    <main className="bg-stilo-bg" data-testid="consultations-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24">
        <p className="overline">Asesorías</p>
        <h1 className="font-serif text-6xl lg:text-7xl mt-4 leading-[0.95] max-w-3xl font-light">Elige la conversación que necesita tu casa.</h1>
        <p className="mt-6 text-stilo-ink2 max-w-xl">Pago seguro con Stripe. Tras reservar, accederás a tu hilo privado donde compartiremos mensajes con audio y vídeo.</p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 pb-32">
          {pkgs.map((p, i) => {
            const Icon = ICONS[p.id] || Sparkles;
            const featured = i === 1;
            return (
              <div key={p.id} className={`relative p-10 border flex flex-col ${featured ? 'bg-stilo-ink text-stilo-bg border-stilo-ink' : 'bg-stilo-beige border-stilo-ink/10'}`} data-testid={`package-${p.id}`}>
                {featured && <span className="overline absolute top-6 right-6 text-stilo-gold">Más reservada</span>}
                <Icon className={`w-7 h-7 ${featured ? 'text-stilo-gold' : 'text-stilo-ink2'}`} strokeWidth={1.4} />
                <h2 className="font-serif text-4xl mt-8 font-light">{p.name}</h2>
                <p className={`mt-3 leading-relaxed ${featured ? 'text-stilo-stone/85' : 'text-stilo-ink2'}`}>{p.description}</p>
                <div className="mt-10 flex items-end justify-between flex-1">
                  <div>
                    <span className="font-serif text-5xl">{fmtPrice(p.price, p.currency)}</span>
                    <p className={`overline mt-1 ${featured ? 'text-stilo-stone/60' : 'text-stilo-ink3'}`}>IVA incluido · {p.price_label}</p>
                  </div>
                  <span className={`overline ${featured ? 'text-stilo-stone/70' : 'text-stilo-ink3'} text-right`}>{p.modality === 'live' ? `${p.duration_minutes} min · Zoom` : 'Asíncrono · 48h'}</span>
                </div>
                <button onClick={() => book(p)} disabled={loading === p.id} className={`mt-10 inline-flex items-center gap-3 text-sm tracking-widest uppercase pb-2 border-b transition-colors ${featured ? 'border-stilo-bg hover:text-stilo-gold hover:border-stilo-gold' : 'border-stilo-ink hover:text-stilo-gold hover:border-stilo-gold'} disabled:opacity-50`} data-testid={`book-${p.id}`}>
                  {loading === p.id ? 'Procesando…' : 'Reservar'} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <Link to={`/asesorias/${p.id}`} className={`block mt-4 text-xs ${featured ? 'text-stilo-stone/70 hover:text-stilo-bg' : 'text-stilo-ink3 hover:text-stilo-ink'} transition-colors`} data-testid={`details-${p.id}`}>
                  Ver qué incluye →
                </Link>
              </div>
            );
          })}

          {/* Custom project CTA card */}
          <div className="relative p-10 border border-stilo-gold/40 bg-stilo-bg flex flex-col" data-testid="package-custom">
            <span className="overline absolute top-6 right-6 text-stilo-gold">A medida</span>
            <Layers className="w-7 h-7 text-stilo-gold" strokeWidth={1.4} />
            <h2 className="font-serif text-4xl mt-8 font-light">Proyecto a medida</h2>
            <p className="mt-3 leading-relaxed text-stilo-ink2">
              Para una estancia entera o una vivienda completa. Distribución, iluminación, mobiliario, materiales — un proyecto cerrado y diseñado para ti.
            </p>
            <div className="mt-10 flex items-end justify-between flex-1">
              <div>
                <span className="font-serif text-5xl text-stilo-ink">A consultar</span>
                <p className="overline mt-1 text-stilo-ink3">Presupuesto personalizado</p>
              </div>
              <span className="overline text-stilo-ink3 text-right">Sesión de valoración gratuita</span>
            </div>
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer"
              className="mt-10 inline-flex items-center gap-3 text-sm tracking-widest uppercase pb-2 border-b border-stilo-ink hover:text-stilo-gold hover:border-stilo-gold transition-colors"
              data-testid="contact-custom">
              Reservar valoración <Calendar className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href="https://www.studiostiloid.com/contacto/" target="_blank" rel="noreferrer"
              className="block mt-4 text-xs text-stilo-ink3 hover:text-stilo-ink transition-colors" data-testid="contact-web">
              O contáctanos por la web →
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
